import { FilterList } from "@mui/icons-material"
import {
  Box,
  Button,
  Checkbox,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material"
import { CollapsibleCard } from "components/CollapsiblCard/CollapsibleCard"
import { Examination, Project, TaskStep } from "graphql/types"
import { useEffect, useState } from "react"
import { getUniqueTaskTypes } from "utils/getUniqueTaskTypes"
import { getTextForTaskStep } from "utils/taskSteps"

type ReportRightHeaderProps = {
  examination: Examination
  project: Project
  handleFilter: (filter: TaskStep[]) => void
}

export const ReportRightHeader = (props: ReportRightHeaderProps) => {
  const { examination, handleFilter, project } = props
  const [openProject, setOpenProject] = useState(false)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [selectedTaskTypes, setSelectedTaskTypes] = useState<TaskStep[]>([])
  const [isInitialized, setIsInitialized] = useState(false)

  const uniqueTaskTypes = getUniqueTaskTypes(examination)
  const isFilterMenuOpen = Boolean(anchorEl)

  // Initialize selected task types with all available types
  useEffect(() => {
    if (!isInitialized && uniqueTaskTypes.length > 0) {
      setSelectedTaskTypes(uniqueTaskTypes)
      setIsInitialized(true)
      handleFilter(uniqueTaskTypes)
    }
  }, [uniqueTaskTypes, isInitialized, handleFilter])

  const handleFilterMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleFilterMenuClose = () => {
    setAnchorEl(null)
  }

  const handleTaskTypeToggle = (taskType: TaskStep) => {
    const newSelectedTypes = selectedTaskTypes.includes(taskType)
      ? selectedTaskTypes.filter((type) => type !== taskType)
      : [...selectedTaskTypes, taskType]

    setSelectedTaskTypes(newSelectedTypes)
    handleFilter(newSelectedTypes)
  }

  const handleSelectAll = () => {
    setSelectedTaskTypes(uniqueTaskTypes)
    handleFilter(uniqueTaskTypes)
  }

  const handleDeselectAll = () => {
    setSelectedTaskTypes([])
    handleFilter([])
  }

  return (
    <>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
        <Typography variant="h5">Related data / notes</Typography>
        <IconButton
          onClick={handleFilterMenuOpen}
          size="small"
          sx={{
            color: "primary.main",
            "&:hover": {
              backgroundColor: "primary.light",
              color: "white",
            },
          }}
        >
          <FilterList />
        </IconButton>
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={isFilterMenuOpen}
        onClose={handleFilterMenuClose}
        PaperProps={{
          sx: {
            minWidth: 250,
            maxHeight: 400,
            overflow: "auto",
          },
        }}
      >
        <Box sx={{ p: 1, borderBottom: 1, borderColor: "divider" }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: "bold" }}>
            Tasktypen filtern
          </Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button size="small" variant="outlined" onClick={handleSelectAll} sx={{ flex: 1 }}>
              Alles auswählen
            </Button>
            <Button size="small" variant="outlined" onClick={handleDeselectAll} sx={{ flex: 1 }}>
              Alles abwählen
            </Button>
          </Box>
        </Box>

        {uniqueTaskTypes.map((taskType) => (
          <MenuItem key={taskType} onClick={() => handleTaskTypeToggle(taskType)} sx={{ py: 0.5 }}>
            <Checkbox checked={selectedTaskTypes.includes(taskType)} size="small" />
            <Typography variant="body2">{getTextForTaskStep(taskType)}</Typography>
          </MenuItem>
        ))}
      </Menu>

      <Box sx={{ mb: 2 }}>
        <CollapsibleCard
          open={openProject}
          title={project.name}
          subheader={examination.name}
          onToggle={() => setOpenProject(!openProject)}
        >
          <Typography variant="body2">Projekt</Typography>
          <Divider />
          {project.description && project.description !== "" && (
            <Box>
              <Typography variant="body2">{project.description}</Typography>
            </Box>
          )}
          {project.specialArrangements && project.specialArrangements !== "" && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption">Sonderabsprachen:</Typography>
              <Typography variant="body2">{project.specialArrangements}</Typography>
            </Box>
          )}
          {project.notes && project.notes !== "" && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption">Notizen:</Typography>
              <Typography variant="body2">{project.notes}</Typography>
            </Box>
          )}

          <Typography sx={{ mt: 2 }} variant="body2">
            Prüfverfahren
          </Typography>
          <Divider />

          {examination.specialArrangements && examination.specialArrangements !== "" && (
            <Box>
              <Typography variant="caption">Sonderabsprachen:</Typography>
              <Typography variant="body2">{examination.specialArrangements}</Typography>
            </Box>
          )}

          {examination.deviations && examination.deviations !== "" && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption">Abweichungen:</Typography>
              <Typography variant="body2">{examination.deviations}</Typography>
            </Box>
          )}

          {examination.calculation.offerText &&
            examination.calculation.offerText !== examination.name && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption">Angebotstext:</Typography>
                <Typography variant="body2">{examination.calculation.offerText}</Typography>
              </Box>
            )}
        </CollapsibleCard>
      </Box>

      <Divider sx={{ my: 2 }} />
    </>
  )
}
